// Arène miniature derrière les menus : formes et tanks bots dessinés comme en jeu, simulés dans le navigateur
// (aucun réseau, aucune donnée de partie). Coupée avec « animations réduites » et quand l'onglet est caché.
import { Application, Container, Graphics, TilingSprite, type Texture } from 'pixi.js';
import { CLASSES, type ClassNode } from '../../../shared/catalog/classes';
import { SHAPES } from '../../../shared/shapes';
import { colors } from '../game/colors';
import { ART_R, SHAPE_LOOKS, drawShape } from '../game/render/art';
import { drawTankParts } from '../game/render/variants';
import { Particles } from '../game/render/particles';

const W = 3000;
const H = 1900;
const VIEW_W = 1500;
const VIEW_H = 860;

interface Shape {
  g: Graphics;
  kind: number;
  x: number;
  y: number;
  vx: number;
  vy: number;
  vr: number;
  hp: number;
  max: number;
  r: number;
  dead: number;
  flash: number;
}

interface Bot {
  root: Container;
  hull: Graphics;
  turret: Graphics;
  auto: Graphics;
  cls: ClassNode;
  x: number;
  y: number;
  heading: number;
  aim: number;
  target: Shape | null;
  cool: number;
  reload: number;
  accent: number;
  orbit: number;
  kick: number;
}

interface Shot {
  g: Graphics;
  x: number;
  y: number;
  vx: number;
  vy: number;
  r: number;
  life: number;
}

/** Tirage pondéré proche d'une zone verte (formes rares un peu plus fréquentes pour la vitrine). */
const WEIGHTS = [48, 28, 15, 7, 2];
function pickKind(): number {
  let r = Math.random() * 100;
  for (let i = 0; i < WEIGHTS.length; i++) {
    r -= WEIGHTS[i];
    if (r <= 0) return i;
  }
  return 0;
}

export class MenuArena {
  private world = new Container();
  private shapeLayer = new Container();
  private shotLayer = new Container();
  private botLayer = new Container();
  private fxLayer = new Container();
  private particles: Particles;
  private shapes: Shape[] = [];
  private bots: Bot[] = [];
  private shots: Shot[] = [];
  private templates: Graphics[] = [];
  private t = Math.random() * 100;
  private destroyed = false;

  private constructor(
    private readonly app: Application,
    private readonly host: HTMLElement,
    private readonly still: boolean,
  ) {
    this.particles = new Particles(this.fxLayer);
    app.stage.addChild(this.world);
    this.buildGround();
    this.world.addChild(this.shapeLayer, this.shotLayer, this.botLayer, this.fxLayer);
    for (const def of SHAPES) {
      const g = new Graphics();
      drawShape(g, def);
      this.templates.push(g);
    }
    const n = Math.round(34 * Math.min(1.4, Math.max(0.6, (host.clientWidth * host.clientHeight) / (1600 * 900))));
    for (let i = 0; i < n; i++) this.spawnShape(true);
    const pool = CLASSES.filter((c) => c.status === 'implemented' && c.id !== 'BASE' && c.weapon.barrels.some((b) => !b.auto));
    const pal = colors();
    const accents = [pal.enemy, pal.bot, pal.bot, pal.enemy, pal.bot];
    for (let i = 0; i < 5; i++) this.spawnBot(pool[Math.floor(Math.random() * pool.length)], accents[i]);
    this.resize();
    // Quelques secondes simulées d'avance : la scène est déjà en mouvement à l'ouverture.
    for (let i = 0; i < 90; i++) this.step(1 / 30);
    if (still) app.render();
    else {
      app.ticker.maxFPS = 30;
      app.ticker.add((tk) => this.step(Math.min(0.1, tk.deltaMS / 1000)));
    }
  }

  static async create(host: HTMLElement, opts: { still: boolean; quality: number }): Promise<MenuArena> {
    const app = new Application();
    await app.init({
      resizeTo: host,
      background: 0x151a20,
      antialias: true,
      autoDensity: true,
      resolution: Math.min(1.5, window.devicePixelRatio || 1) * opts.quality,
      preference: 'webgl',
      powerPreference: 'low-power',
    });
    app.canvas.classList.add('menu-bg-canvas');
    host.appendChild(app.canvas);
    return new MenuArena(app, host, opts.still);
  }

  private buildGround(): void {
    const cell = 48;
    const g = new Graphics();
    g.rect(0, 0, cell * 4, cell * 4).fill(0x151a20);
    for (let i = 0; i <= 4; i++) {
      const major = i === 0;
      g.moveTo(i * cell, 0).lineTo(i * cell, cell * 4).stroke({ width: major ? 2 : 1, color: 0x232a33 });
      g.moveTo(0, i * cell).lineTo(cell * 4, i * cell).stroke({ width: major ? 2 : 1, color: 0x232a33 });
    }
    const tex = this.app.renderer.generateTexture(g) as Texture;
    const tiles = new TilingSprite({ texture: tex, width: W + 800, height: H + 800 });
    tiles.position.set(-400, -400);
    this.world.addChild(tiles);
  }

  resize(): void {
    const w = this.host.clientWidth || 1;
    const h = this.host.clientHeight || 1;
    const s = Math.max(w / VIEW_W, h / VIEW_H);
    this.world.scale.set(s);
    if (this.still) this.app.render();
  }

  private spawnShape(anywhere: boolean): void {
    const kind = pickKind();
    const def = SHAPES[kind];
    const g = new Graphics(this.templates[kind].context);
    const s: Shape = {
      g,
      kind,
      x: 80 + Math.random() * (W - 160),
      y: 80 + Math.random() * (H - 160),
      vx: (Math.random() - 0.5) * 8,
      vy: (Math.random() - 0.5) * 8,
      vr: (Math.random() - 0.5) * 0.5,
      hp: def.hp / 3,
      max: def.hp / 3,
      r: def.radius,
      dead: 0,
      flash: 0,
    };
    g.position.set(s.x, s.y);
    g.rotation = Math.random() * Math.PI * 2;
    g.alpha = anywhere ? 1 : 0;
    this.shapeLayer.addChild(g);
    this.shapes.push(s);
  }

  private spawnBot(cls: ClassNode, accent: number): void {
    const root = new Container();
    const parts = { hull: new Graphics(), turret: new Graphics(), auto: new Graphics() };
    drawTankParts(parts, cls, 1 + Math.floor(Math.random() * 5), accent);
    root.addChild(parts.hull, parts.turret, parts.auto);
    root.scale.set(1.1);
    const b: Bot = {
      root,
      ...parts,
      cls,
      x: 300 + Math.random() * (W - 600),
      y: 300 + Math.random() * (H - 600),
      heading: Math.random() * Math.PI * 2,
      aim: 0,
      target: null,
      cool: Math.random(),
      reload: 0.45 + Math.random() * 0.4,
      accent,
      orbit: Math.random() < 0.5 ? 1 : -1,
      kick: 0,
    };
    this.botLayer.addChild(root);
    this.bots.push(b);
  }

  private pickTarget(b: Bot): Shape | null {
    let best: Shape | null = null;
    let bd = Infinity;
    for (const s of this.shapes) {
      if (s.dead > 0) continue;
      if (this.bots.some((o) => o !== b && o.target === s)) continue;
      const d = Math.hypot(s.x - b.x, s.y - b.y) - s.kind * 120;
      if (d < bd) {
        bd = d;
        best = s;
      }
    }
    return best;
  }

  private step(dt: number): void {
    if (this.destroyed) return;
    this.t += dt;
    for (const s of this.shapes) {
      if (s.dead > 0) {
        s.dead -= dt;
        if (s.dead <= 0) {
          s.x = 80 + Math.random() * (W - 160);
          s.y = 80 + Math.random() * (H - 160);
          s.hp = s.max;
          s.g.alpha = 0;
          s.g.visible = true;
        }
        continue;
      }
      s.x = Math.min(W - 40, Math.max(40, s.x + s.vx * dt));
      s.y = Math.min(H - 40, Math.max(40, s.y + s.vy * dt));
      s.g.rotation += s.vr * dt;
      s.g.position.set(s.x, s.y);
      if (s.g.alpha < 1) s.g.alpha = Math.min(1, s.g.alpha + dt * 1.5);
      s.flash = Math.max(0, s.flash - dt * 6);
      s.g.scale.set(1 + s.flash * 0.06);
    }
    for (const b of this.bots) this.stepBot(b, dt);
    for (let i = this.shots.length - 1; i >= 0; i--) {
      const sh = this.shots[i];
      sh.life -= dt;
      sh.x += sh.vx * dt;
      sh.y += sh.vy * dt;
      sh.g.position.set(sh.x, sh.y);
      let hit: Shape | null = null;
      for (const s of this.shapes) {
        if (s.dead > 0 || s.g.alpha < 0.6) continue;
        if (Math.hypot(s.x - sh.x, s.y - sh.y) < s.r + sh.r) {
          hit = s;
          break;
        }
      }
      if (hit) {
        hit.hp -= 10;
        hit.flash = 1;
        hit.vx += sh.vx * 0.01;
        hit.vy += sh.vy * 0.01;
        if (hit.hp <= 0) {
          const look = SHAPE_LOOKS[hit.kind];
          this.particles.burst(hit.x, hit.y, look.fill, 8 + hit.kind * 3, 220, 4 + hit.kind, SHAPES[hit.kind].sides, 0.6);
          this.particles.flash(hit.x, hit.y, look.fill, hit.r * 0.8, 0.16);
          hit.dead = 3 + Math.random() * 3;
          hit.g.visible = false;
          for (const b of this.bots) if (b.target === hit) b.target = null;
        }
      }
      if (hit || sh.life <= 0) {
        sh.g.destroy();
        this.shots.splice(i, 1);
      }
    }
    this.particles.update(dt);
    // Caméra : lente dérive en huit sur l'arène.
    const cx = W / 2 + Math.sin(this.t * 0.045) * (W / 2 - VIEW_W / 2 - 40);
    const cy = H / 2 + Math.sin(this.t * 0.07) * (H / 2 - VIEW_H / 2 - 40);
    const s = this.world.scale.x;
    this.world.position.set(this.host.clientWidth / 2 - cx * s, this.host.clientHeight / 2 - cy * s);
  }

  private stepBot(b: Bot, dt: number): void {
    if (!b.target || b.target.dead > 0) b.target = this.pickTarget(b);
    const tg = b.target;
    let mx = 0;
    let my = 0;
    if (tg) {
      const dx = tg.x - b.x;
      const dy = tg.y - b.y;
      const d = Math.hypot(dx, dy) || 1;
      const want = 260 + tg.r;
      // Approche jusqu'à distance de tir, puis tourne autour de la cible.
      const radial = d > want + 40 ? 1 : d < want - 40 ? -0.6 : 0;
      mx = (dx / d) * radial + (-dy / d) * 0.55 * b.orbit;
      my = (dy / d) * radial + (dx / d) * 0.55 * b.orbit;
      let da = Math.atan2(dy, dx) - b.aim;
      da = Math.atan2(Math.sin(da), Math.cos(da));
      b.aim += da * Math.min(1, dt * 5);
      b.cool -= dt;
      if (b.cool <= 0 && Math.abs(da) < 0.25 && d < 720) {
        b.cool = b.reload;
        this.fire(b);
      }
    }
    // Les tanks s'écartent un peu les uns des autres et des bords.
    for (const o of this.bots) {
      if (o === b) continue;
      const dx = b.x - o.x;
      const dy = b.y - o.y;
      const d = Math.hypot(dx, dy);
      if (d > 0 && d < 160) {
        mx += (dx / d) * (1 - d / 160);
        my += (dy / d) * (1 - d / 160);
      }
    }
    if (b.x < 200) mx += 1;
    if (b.x > W - 200) mx -= 1;
    if (b.y < 200) my += 1;
    if (b.y > H - 200) my -= 1;
    const m = Math.hypot(mx, my);
    if (m > 0.05) {
      const speed = 115;
      b.x += (mx / m) * speed * dt;
      b.y += (my / m) * speed * dt;
      const want = Math.atan2(my, mx);
      let dh = want - b.heading;
      dh = Math.atan2(Math.sin(dh), Math.cos(dh));
      b.heading += dh * Math.min(1, dt * 4);
    }
    b.kick = Math.max(0, b.kick - dt * 6);
    b.root.position.set(b.x, b.y);
    b.hull.rotation = b.heading;
    b.turret.rotation = b.aim;
    b.turret.position.set(-Math.cos(b.aim) * b.kick * 3, -Math.sin(b.aim) * b.kick * 3);
    b.auto.rotation = b.aim + Math.sin(this.t) * 0.4;
  }

  private fire(b: Bot): void {
    b.kick = 1;
    for (const br of b.cls.weapon.barrels) {
      if (br.auto) continue;
      const a = b.aim + (br.angle * Math.PI) / 180 + (Math.random() - 0.5) * 0.06;
      const k = 1.1;
      const ox = -Math.sin(a) * br.offset * ART_R * k;
      const oy = Math.cos(a) * br.offset * ART_R * k;
      const L = br.length * ART_R * k;
      const x = b.x + Math.cos(a) * L + ox;
      const y = b.y + Math.sin(a) * L + oy;
      const r = Math.max(5, Math.min(14, br.width * ART_R * k * 0.42));
      const g = new Graphics().circle(0, 0, r).fill(b.accent).stroke({ width: 2.5, color: 0x4a1a10 });
      g.position.set(x, y);
      this.shotLayer.addChild(g);
      this.shots.push({ g, x, y, vx: Math.cos(a) * 520, vy: Math.sin(a) * 520, r, life: 1.5 });
      if (this.shots.length > 140) this.shots.shift()?.g.destroy();
    }
  }

  setPaused(paused: boolean): void {
    if (this.destroyed || this.still) return;
    if (paused) this.app.ticker.stop();
    else this.app.ticker.start();
  }

  destroy(): void {
    if (this.destroyed) return;
    this.destroyed = true;
    this.app.destroy({ removeView: true }, { children: true });
    // Gabarits hors scène : leurs contextes de dessin étaient partagés par les formes, détruites ci-dessus.
    for (const g of this.templates) g.destroy();
  }
}
